/*
 * Copyright (c) 2026 Bolmate B.V.
 */

import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import Account, {ImportingStat} from '../../models/accountModel';
import BolProduct from '../../models/bolProductModel';
import {ActivateHandle} from '../../util/interfaces';
import {ProductSelectionHelper} from '../../util/productSelectionHelper';
import {
    ClauseType, ProductSelection as SelectionType, ProductSelectionClauseType, ProductSelectionList,
    ProductSelectionParam
} from '../../util/types';
import {createErrorMessage} from '../../util/utils';
import {AppContext} from '../misc/appContext';
import ProductSelection from './productSelection';

interface Props {
    onSubmit: (selection: SelectionType) => void;
    loading: boolean;
}

type LoadParams = {search?: string; selection?: SelectionType};

const LOAD_SELECTION_PRODUCTS = 25;
const DEBOUNCE = 500;
const SYNC_TIMEOUT = 5000;

function ProductSelectionController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {onSubmit} = props;
    const [active, setActive] = useState(false);
    const [availableProducts, setAvailableProducts] = useState<Array<BolProduct>>([]);
    const [selectedProducts, setSelectedProducts] = useState<Array<BolProduct>>([]);
    // TODO Might not need to be a state
    const [selection, setSelection] = useState<SelectionType>({clauses: []});
    const [loadingMore, setLoadingMore] = useState<ProductSelectionParam<boolean>>({available: false, selected: false});
    const [loading, setLoading] = useState<ProductSelectionParam<boolean>>({available: false, selected: false});
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState<ProductSelectionParam<number>>({available: 0, selected: 0});
    const [importing, setImporting] = useState(false);
    const searchDebounce = useRef<number | undefined>(undefined);
    const importTimeout = useRef<number | undefined>(undefined);

    function onActivate(selection?: SelectionType) {
        if (selection)
            setSelection(selection);
        setActive(true);
        loadAll({selection});
    }

    function onClose() {
        setActive(false);
        setAvailableProducts([]);
        setSelectedProducts([]);
        setSelection({clauses: []});
        setLoadingMore({available: false, selected: false});
        setLoading({available: false, selected: false});
        setSearch('');
        setTotal({available: 0, selected: 0});
    }

    function onChangeSearch(value: string) {
        clearTimeout(searchDebounce);
        setSearch(value);
        searchDebounce.current = window.setTimeout(() => {
            loadAll({search: value});
        }, DEBOUNCE);
    }

    function onClearSearch() {
        clearTimeout(searchDebounce);
        setSearch('');
        loadAll({search: ''});
        const searchInput = document.getElementById('product-select-search-input');
        if (searchInput) searchInput.focus();
    }

    function onEnterSearch() {
        clearTimeout(searchDebounce);
        loadAll();
    }

    function onImportProducts() {
        if (!account) return;
        BolProduct.importAsync(account.id, false, (_: Account, success: boolean, data: ResponseData) => {
            if (success) {
                showSnackbar('De producten worden geïmporteerd');
                checkProductImport();
            } else
                showDialog(createErrorMessage(data.message ?? 'Het importeren van je producten is niet gelukt.'),
                    'Importeren mislukt');
        });
    }

    function checkProductImport() {
        clearTimeout(importTimeout);
        if (!account) return;
        account.getImportingProductsCount().then((data: ImportingStat) => {
            const {importing} = data;
            setImporting(importing);
            if (importing) importTimeout.current = window.setTimeout(checkProductImport, SYNC_TIMEOUT);
            else loadAll();
        }).catch((error?: string) => {
            setImporting(false);
            if (error) showDialog(createErrorMessage(error), 'Importeren mislukt');
        });
    }

    function onSelectProduct(bolProduct: BolProduct, clauseType: ClauseType) {
        return function() {
            const clauses = new ProductSelectionHelper(selection.clauses).selectProduct(bolProduct.id,
                clauseType);
            const newSelection = {clauses};
            setSelection(newSelection);
            let selected = selectedProducts.slice();
            let selectedCount = total.selected;
            let available = availableProducts.slice();
            let availableCount = total.available;
            if (clauseType === 'include') {
                selected = [bolProduct, ...selected];
                selectedCount += 1;
                available = available.filter((product) => product.id !== bolProduct.id);
                availableCount -= 1;
            } else {
                selected = selected.filter((product) => product.id !== bolProduct.id);
                selectedCount -= 1;
                available = [bolProduct, ...available];
                availableCount += 1;
            }
            setSelectedProducts(selected);
            setAvailableProducts(available);
            setTotal({selected: selectedCount, available: availableCount});
            // loadAll({selection: newSelection});
        };
    }

    function onSelectAll(clauseType: ClauseType) {
        return function() {
            clearTimeout(searchDebounce);
            const helper = new ProductSelectionHelper(selection.clauses);
            let clauses: Array<ProductSelectionClauseType>;
            if (search.length) clauses = helper.selectQuery(search, clauseType);
            else clauses = helper.selectAll(clauseType);
            const newSelection = {clauses};
            setSelection(newSelection);
            loadAll({selection: newSelection});
        };
    }

    function onSelectRemaining(value: boolean) {
        clearTimeout(searchDebounce);
        const clauseType: ClauseType = value ? 'include' : 'exclude';
        const clauses = new ProductSelectionHelper(selection.clauses).selectRemaining(clauseType);
        setSelection({clauses});
        loadAll({selection: {clauses}});
    }

    function onLoadMore(listType: ProductSelectionList) {
        return function(startIndex: number): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!account) return reject();
                setLoadingMore({...loadingMore, [listType]: true});
                const page = Math.floor(startIndex / LOAD_SELECTION_PRODUCTS);
                BolProduct.getSelection(
                    {
                        accountID: account.id, limit: LOAD_SELECTION_PRODUCTS, search, page, clauses: selection.clauses,
                        invert: listType === 'available'
                    }, (products: Array<BolProduct>, success: boolean) => {
                        setLoadingMore({...loadingMore, [listType]: false});
                        if (!success) return reject();
                        if (listType === 'available') setAvailableProducts(availableProducts.concat(products));
                        else setSelectedProducts(selectedProducts.concat(products));
                        resolve();
                    });
            });
        };
    }

    function _onSubmit() {
        onSubmit(selection);
    }

    function loadProducts(listType: ProductSelectionList, params?: LoadParams) {
        if (!account) return false;
        const _selection = params?.selection ?? selection;
        // No need to load products if remaining is selected
        if (new ProductSelectionHelper(_selection.clauses).isRemainingSelected()) return;
        setLoading({...loading, [listType]: true});
        BolProduct.getSelection(
            {
                accountID: account.id, limit: LOAD_SELECTION_PRODUCTS, search: params?.search ?? search, page: 0,
                clauses: _selection.clauses, invert: listType === 'available'
            }, (products: Array<BolProduct>, success: boolean, data: ResponseData) => {
                setLoading({...loading, [listType]: false});
                if (success) {
                    if (listType === 'available') setAvailableProducts(products);
                    else setSelectedProducts(products);
                    setTotal((currentTotal) => ({...currentTotal, [listType]: data.total}));
                } else showDialog(createErrorMessage(data.message ?? 'Het is niet gelukt om de producten te laden.'),
                    'Producten laden mislukt');
            });
    }

    function loadAll(params?: LoadParams) {
        loadProducts('available', params);
        loadProducts('selected', params);
    }

    function clearTimeout(ref: any) {
        window.clearTimeout(ref.current);
        ref.current = undefined;
    }

    useImperativeHandle(ref, () => ({activate: onActivate, close: onClose}));

    useEffect(() => {
        return function() {
            clearTimeout(searchDebounce);
        };
    }, []);

    const helper = new ProductSelectionHelper(selection.clauses);
    return (
        <ProductSelection onClose={onClose} onChangeSearch={onChangeSearch} onEnterSearch={onEnterSearch}
                          onImportProducts={onImportProducts} onSelectProduct={onSelectProduct}
                          onSelectAll={onSelectAll} onSelectRemaining={onSelectRemaining} onLoadMore={onLoadMore}
                          onSubmit={_onSubmit} onClearSearch={onClearSearch} availableProducts={availableProducts}
                          selectedProducts={selectedProducts} loadingMore={loadingMore} loading={loading}
                          parentLoading={props.loading} search={search} total={total} active={active}
                          remainingSelected={helper.isRemainingSelected()} allSelected={helper.isAllSelected()}
                          anySelected={helper.isAnySelected()} importing={importing}/>
    );
}

ProductSelectionController.displayName = 'ProductSelectionController';

export default forwardRef<ActivateHandle, Props>(ProductSelectionController);